/**
 * API Configuration
 * Centralized configuration for all API-related settings
 */

/**
 * Check if code is running on server
 */
export const isServer = typeof window === 'undefined';

/**
 * Build-time env snapshot for the browser fallback below.
 *
 * A dynamic `import.meta.env[key]` read forces Vite to inline the ENTIRE env
 * object into the bundle — every VITE_* var set at build time, including
 * dev-only credentials — even when the reading code never uses them (#580).
 * Snapshotting the keys we actually serve keeps the inlined surface to this
 * explicit allowlist. The server path still prefers live process.env.
 */
export const BUILD_TIME_ENV: Record<string, string | undefined> = {
  VITE_API_BASE_URL: import.meta.env.VITE_API_BASE_URL,
  VITE_COOKIE_PREFIX: import.meta.env.VITE_COOKIE_PREFIX,
  VITE_APP_ENV: import.meta.env.VITE_APP_ENV,
  VITE_DEV_MODE: import.meta.env.VITE_DEV_MODE,
  VITE_ASSETS_URL: import.meta.env.VITE_ASSETS_URL,
};

/**
 * Get environment variable with type safety.
 *
 * Precedence is runtime-correct (issue #670): in an SSR Vite bundle
 * `import.meta.env` is a build-time snapshot that cannot see runtime-only
 * values (e.g. TIL_API_INTERNAL_URL, which is not VITE_-prefixed), so the Node
 * server reads `process.env` FIRST and falls back to the baked build-time env. The browser
 * can only ever see the build-time env (Vite inlines it), so it never touches
 * `process.env`.
 */
export function getEnvVar(key: string, defaultValue: string): string {
  // Server-side (Node.js): prefer the live process.env (runtime-only values
  // like TIL_API_INTERNAL_URL are absent from the build-time snapshot), then
  // fall back to the Vite build-time env so SSR matches the client.
  if (isServer && typeof process !== 'undefined' && process.env) {
    const value = process.env[key];
    if (value !== undefined && value !== '') return value;
  }

  // Browser (Vite) — and server fallback: build-time env allowlist.
  return BUILD_TIME_ENV[key] || defaultValue;
}

/**
 * Check if dev mode is enabled
 */
export const DEV_MODE = getEnvVar('VITE_DEV_MODE', 'false') === 'true';

/**
 * Cookie prefix for environment separation
 * Used to separate production and staging cookies (e.g., "stg_" for staging)
 * Default is empty string for production
 */
export const COOKIE_PREFIX = getEnvVar('VITE_COOKIE_PREFIX', '');

/** Assets CDN origin; every asset URL in the app is built from this. */
export const ASSETS_BASE_URL = getEnvVar('VITE_ASSETS_URL', 'https://assets.til.sg');

/**
 * Helper function to get prefixed cookie name
 */
export function getCookieName(name: string): string {
  return COOKIE_PREFIX ? `${COOKIE_PREFIX}${name}` : name;
}

/**
 * Canonical environment → API origin → cookie-prefix tuples (issue #693).
 * Any two known values that resolve to different tuples are a
 * production/staging mix-up and must fail closed before serving.
 */
const KNOWN_TUPLES = [
  { env: 'development', origin: 'api.til.test', prefix: '' },
  { env: 'staging', origin: 'stg-api.til.sg', prefix: 'stg_' },
  { env: 'production', origin: 'api.til.sg', prefix: '' },
] as const;

type EnvTuple = (typeof KNOWN_TUPLES)[number];
type TupleDimension = 'env' | 'origin' | 'prefix';

const TUPLES_BY: Record<TupleDimension, (value: string) => readonly EnvTuple[]> = {
  env: (value) => KNOWN_TUPLES.filter((t) => t.env === value),
  origin: (value) => KNOWN_TUPLES.filter((t) => t.origin === value),
  prefix: (value) => KNOWN_TUPLES.filter((t) => t.prefix === value),
};

function hostnameOf(url: string): string | null {
  try {
    return new URL(url).hostname;
  } catch {
    return null;
  }
}

/**
 * Validate that VITE_APP_ENV, the API origin, and the cookie prefix all point
 * at the same environment. Values outside the known tuples (custom domains,
 * localhost, tests) are ignored — only known production/staging mismatches
 * are rejected. Returns human-readable violations (empty = valid).
 */
export function validateEnvConsistency(
  appEnv: string,
  apiBaseUrl: string,
  cookiePrefix: string
): string[] {
  const origin = hostnameOf(apiBaseUrl) ?? '';
  const violations: string[] = [];
  // Fail closed on the security-relevant case (no-mistakes #985): a production
  // origin with an unset/unknown VITE_APP_ENV is a production deployment that
  // omitted the variable — the admin-login route would otherwise be enabled
  // by the old `!== 'production'` guard. Requiring the env to be explicit here
  // makes the deployment fail instead of silently enabling the route.
  const envTuples = TUPLES_BY.env(appEnv);
  if (
    envTuples.length === 0 &&
    origin !== '' &&
    TUPLES_BY.origin(origin).some((t) => t.env === 'production')
  ) {
    violations.push(
      `env=${JSON.stringify(appEnv)} is unset/unknown but the API origin ${JSON.stringify(origin)} is production - set VITE_APP_ENV explicitly`
    );
  }
  const pairs: Array<[TupleDimension, string, TupleDimension, string]> = [
    ['env', appEnv, 'origin', origin ?? ''],
    ['env', appEnv, 'prefix', cookiePrefix],
    ['origin', origin ?? '', 'prefix', cookiePrefix],
  ];
  for (const [dimA, valueA, dimB, valueB] of pairs) {
    const tuplesA = TUPLES_BY[dimA](valueA);
    const tuplesB = TUPLES_BY[dimB](valueB);
    // Unknown value (not in any canonical tuple) — not a known mismatch.
    if (tuplesA.length === 0 || tuplesB.length === 0) continue;
    if (!tuplesB.some((t) => tuplesA.includes(t))) {
      violations.push(
        `${dimA}=${JSON.stringify(valueA)} conflicts with ${dimB}=${JSON.stringify(valueB)}`
      );
    }
  }
  return violations;
}

/**
 * Fail closed at startup/build: throw when the configured tuple is a known
 * production/staging mismatch (issue #693).
 */
export function assertEnvConsistency(
  appEnv: string,
  apiBaseUrl: string,
  cookiePrefix: string
): void {
  const violations = validateEnvConsistency(appEnv, apiBaseUrl, cookiePrefix);
  if (violations.length > 0) {
    throw new Error(
      `Refusing to start: environment/API/cookie configuration points at conflicting ` +
        `environments (expected one of ${KNOWN_TUPLES.map(
          (t) => `${t.env} -> ${t.origin} (prefix "${t.prefix}")`
        ).join(', ')}). Violations: ${violations.join('; ')}`
    );
  }
}

// Run at import time so a mismatched deployment fails before serving.
assertEnvConsistency(
  getEnvVar('VITE_APP_ENV', ''),
  getEnvVar('VITE_API_BASE_URL', ''),
  getEnvVar('VITE_COOKIE_PREFIX', '')
);

/**
 * API configuration constants
 */
export const API_CONFIG = {
  /**
   * Base URL for the Go backend API
   * REQUIRED: VITE_API_BASE_URL environment variable must be set
   * - Server-side: Can use TIL_API_INTERNAL_URL for better performance (optional)
   * - Client-side: Always uses VITE_API_BASE_URL
   */
  BASE_URL: (() => {
    // FIRST: Check for explicit VITE_API_BASE_URL (REQUIRED)
    const explicitUrl = getEnvVar('VITE_API_BASE_URL', '');
    if (!explicitUrl) {
      throw new Error(
        'VITE_API_BASE_URL environment variable is required. ' +
          'Please set it in your .env file or deployment configuration.'
      );
    }

    // Server-side (SSR): Optionally use Railway internal networking for
    // performance. TIL_API_INTERNAL_URL is a complete origin, e.g.
    // http://stg-go-backend.railway.internal:8080.
    if (isServer) {
      const internalUrl = getEnvVar('TIL_API_INTERNAL_URL', '');
      if (internalUrl) return internalUrl;
    }

    return explicitUrl;
  })(),

  /**
   * Request timeout in milliseconds
   */
  TIMEOUT_MS: 10000,

  /**
   * Number of retry attempts for failed requests.
   * One retry, not three: each attempt can burn up to TIMEOUT_MS plus
   * exponential backoff, and react-query layers its own retry on top — at 3
   * the stacked ladders put a ~47s worst case behind a single failed query
   * before the UI could show anything.
   */
  RETRY_ATTEMPTS: 1,

  /**
   * Base delay for exponential backoff (ms)
   */
  RETRY_BASE_DELAY_MS: 1000,

  /**
   * Maximum delay for exponential backoff (ms)
   */
  RETRY_MAX_DELAY_MS: 10000,

  /**
   * Credentials mode for fetch requests
   */
  CREDENTIALS: 'include' as const,

  /**
   * Default headers for all requests
   */
  DEFAULT_HEADERS: {
    'Content-Type': 'application/json',
  } as const,
} as const;

/**
 * API endpoint paths
 * These are relative paths that will be appended to BASE_URL
 */
export const API_ENDPOINTS = {
  // Health check
  HEALTH: '/health',

  // Authentication
  AUTH: {
    TELEGRAM_INIT: '/v1/auth/telegram/init',
    TELEGRAM_VERIFY: '/v1/auth/telegram/verify',
    ADMIN_LOGIN: '/v1/auth/admin/login',
    SESSION: '/v1/auth/session',
    REFRESH: '/v1/auth/refresh',
    LOGOUT: '/v1/auth/logout',
  },
} as const;

/**
 * HTTP status codes for reference
 */
export const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  UNPROCESSABLE_ENTITY: 422,
  TOO_MANY_REQUESTS: 429,
  INTERNAL_SERVER_ERROR: 500,
  BAD_GATEWAY: 502,
  SERVICE_UNAVAILABLE: 503,
} as const;
